// src/components/Login.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import djLogo from "../media/djs-logo.png";

// Make sure you're importing from the correct path:
import {
  signInWithEmail,
  signUpWithEmail,
  resetPassword
} from "../helper/auth";

export default function Login() {
  const navigate = useNavigate();
  // mode can be: "login", "register", or "reset"
  const [mode, setMode] = useState("login");

  useEffect(() => {
    // Check if the current URL path contains /register
    const isRegisterPath = window.location.pathname.includes('/register');
    setMode(isRegisterPath ? "register" : "login");
  }, []); // Empty dependency array means this runs once on mount

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [loading, setLoading] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    setIsSpinning(true);

    try {
      if (mode === "login") {
        // Login
        await signInWithEmail(email.trim(), password);
        // After successful login, navigate to root
        navigate('/');
      } else if (mode === "register") {
        // Registration
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        await signUpWithEmail(email.trim(), password);
        // After successful registration and auto-login, navigate to root
        navigate('/');
      } else if (mode === "reset") {
        // Password Reset
        await resetPassword(email.trim());
        setSuccess(
          `If the email you entered matches an existing account, you will receive an email from the DJs Team within ten minutes, explaining how to reset your password. (If you don’t see it, please check your spam folder.) If you don’t receive an email, please create an account.`
        );
      }
    } catch (err) {
      // Log the error for debugging
      console.error("Auth Error:", err.code, err.message);
      handleError(err);
    } finally {
      setLoading(false);
      // Keep spinning for a moment after login attempt
      setTimeout(() => setIsSpinning(false), 500);
    }
  };

  /**
   * Handle Firebase errors by mapping specific error codes to user-friendly messages.
   */
  const handleError = (err) => {
    switch (err.code) {
      case "auth/user-not-found":
        // Email doesn't exist in Firebase
        setError("No account found with this email. Please register first.");
        break;
      case "auth/wrong-password":
      case "auth/invalid-credential":
        // Sometimes Firebase returns 'auth/invalid-credential' instead of 'auth/wrong-password'
        setError(
          <span>
            Oops! The email/password combination you entered is incorrect. Please try again or{" "}
            <button
              type="button"
              onClick={() => {
                setMode("reset");
                setError("");
                setSuccess("");
              }}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              reset your password
            </button>
            {" "}if you believe you're using the correct email address. Otherwise,{" "}
            <button
              type="button"
              onClick={() => {
                setMode("register");
                setError("");
                setSuccess("");
              }}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              create a new account here
            </button>
            . If you're stuck, email{" "}
            <a href="mailto:djs@djsatthepolls.org" className="text-indigo-600 hover:text-indigo-800 font-medium">
              djs@djsatthepolls.org
            </a>
            {" "}for help.
          </span>
        );
        break;
      case "auth/too-many-requests":
        setError("Too many failed attempts. Please try again later.");
        break;
      case "auth/invalid-email":
        setError("Invalid email format.");
        break;
      case "auth/weak-password":
        setError("Password is too weak. Please use a stronger password.");
        break;
      case "auth/email-already-in-use":
        setError(
          <span>
            Oops! An account with that email address already exists. Please{" "}
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError("");
                setSuccess("");
              }}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              log in here
            </button>
            {" "}or{" "}
            <button
              type="button"
              onClick={() => {
                setMode("reset");
                setError("");
                setSuccess("");
              }}
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              reset your password
            </button>
            {" "}if needed.
          </span>
        );
        break;
      default:
        if (err.message) {
          // Custom errors (like "Passwords do not match")
          setError(err.message);
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
    }
  };

  // Helper to render the correct heading based on 'mode'
  const renderHeading = () => {
    if (mode === "login") return "Log In";
    if (mode === "register") return "Register";
    return "Reset Password";
  };

  return (
    <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
      {/* Logo & Title */}
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <img 
          alt="DJs Portal" 
          src={djLogo} 
          className={`mx-auto h-32 w-auto transition-all duration-1000 ${
            isSpinning ? 'animate-[spin_1s_ease-in-out_1]' : ''
          }`}
        />
        <h2 className="mt-10 text-left text-2xl font-bold leading-9 tracking-tight text-gray-900">
          DJs Portal
        </h2>
        {(mode === "register" || mode === "login") && (
          <p className="mt-4 text-sm leading-6 text-gray-500/80 max-w-sm">
            <span className="text-gray-400">
            If you haven't applied or worked with us since the 2024 election, click REGISTER to set up a new account. When asked if you've applied or worked with us before, select YES — this links your history and lets you verify that your information is current.
            </span>
          </p>
        )}
      </div>

      {/* Auth Form */}
      <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Email
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900
                  shadow-sm ring-1 ring-inset ring-gray-300 
                  placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-indigo-600 
                  sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          {/* Password Field (not needed in reset mode) */}
          {mode !== "reset" && (
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                {mode === "register" ? "Create Password" : "Password"}
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                    shadow-sm ring-1 ring-inset ring-gray-300 
                    placeholder:text-gray-400 
                    focus:ring-2 focus:ring-inset focus:ring-indigo-600 
                    sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          )}

          {/* Confirm Password (only for register mode) */}
          {mode === "register" && (
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Confirm Password
              </label>
              <div className="mt-2">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 
                    shadow-sm ring-1 ring-inset ring-gray-300 
                    placeholder:text-gray-400 
                    focus:ring-2 focus:ring-inset focus:ring-indigo-600 
                    sm:text-sm sm:leading-6"
                />
              </div>
            </div>
          )}

          {/* Error & Success Messages */}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {success && <p className="mt-2 text-sm text-green-600">{success}</p>}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full justify-center rounded-md bg-primary 
                px-3 py-1.5 text-sm font-semibold leading-6 text-white 
                shadow-sm hover:bg-accent 
                focus-visible:outline focus-visible:outline-2 
                focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {loading ? "Please wait..." : renderHeading()}
            </button>
          </div>
        </form>

        {/* Links to switch modes */}
        <div className="mt-4 text-sm text-gray-600">
          {mode === "login" && (
            <>
              <p className="mb-2">
                Don&apos;t have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("register");
                    setError("");
                    setSuccess("");
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  Register
                </button>
              </p>
              <p className="mb-2">
                Forgot your password?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("reset");
                    setError("");
                    setSuccess("");
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  Reset here
                </button>
              </p>
              <p className="text-sm text-gray-500">
                Questions? Email{" "}
                <a href="mailto:djs@djsatthepolls.org" className="text-indigo-600 hover:text-indigo-800">
                  djs@djsatthepolls.org
                </a>
              </p>
            </>
          )}

          {mode === "register" && (
            <>
              <p>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("login");
                    setError("");
                    setSuccess("");
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  Log In
                </button>
              </p>
              <p className="text-sm text-gray-500">
                Questions? Email{" "}
                <a href="mailto:djs@djsatthepolls.org" className="text-indigo-600 hover:text-indigo-800">
                  djs@djsatthepolls.org
                </a>
              </p>
            </>
          )}

          {mode === "reset" && (
            <>
              <p className="mb-2">
                Remembered your password?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("login");
                    setError("");
                    setSuccess("");
                  }}
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  Log In
                </button>
              </p>
              <p className="text-sm text-gray-500">
                Questions? Email{" "}
                <a href="mailto:djs@djsatthepolls.org" className="text-indigo-600 hover:text-indigo-800">
                  djs@djsatthepolls.org
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
